// Web Push subscription primitive (arc 101 slice 09).
//
// The browser side of "notify me on my devices": register the service worker,
// ask permission, mint a `PushSubscription` against the server's VAPID public
// key, and POST it to `/v1/push/subscriptions` (per-user, server-side). Disabling
// unsubscribes locally and DELETEs the row.
//
// Every browser interaction sits behind an injected `PushRuntime` seam, so the
// enable/disable state-machines are unit-testable in happy-dom (no serviceWorker
// / PushManager / Notification there). `browserPushRuntime` wires the seam to the
// real APIs + `apiFetch`; the component (and tests) pick which to pass.

import { DeadlineExceededError, withDeadline } from '@developerz/kit';
import { apiFetch } from './api';
import { SESSION_TOKEN } from './session';

/** The globals push needs — narrowed so `pushSupported` is fakeable in a test. */
type PushGlobals = {
  navigator?: { serviceWorker?: unknown };
  PushManager?: unknown;
  Notification?: unknown;
};

/**
 * Is Web Push usable here? Needs a service worker, the Push API, and the
 * Notifications API — an old browser, http (non-localhost), or an iOS home-screen
 * gap fails one of these, so the caller hides the toggle rather than dead-ends.
 */
export function pushSupported(g: PushGlobals = globalThis as PushGlobals): boolean {
  return !!g.navigator?.serviceWorker && !!g.PushManager && !!g.Notification;
}

/**
 * Decode a base64url VAPID key to the `BufferSource` `pushManager.subscribe`
 * wants (`applicationServerKey` rejects a bare string in most engines). Pure —
 * unit-tested without a browser.
 */
export function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  // Back the view with a concrete ArrayBuffer (not ArrayBufferLike) so it satisfies
  // `applicationServerKey`'s `BufferSource` without a SharedArrayBuffer widening.
  const output = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

/** Register the app's service worker at root scope. Idempotent — the browser
 *  dedups by (scope, url), so calling it on every load is a no-op after the first. */
export async function registerServiceWorker(
  container: ServiceWorkerContainer = navigator.serviceWorker,
  swUrl = '/sw.js',
): Promise<ServiceWorkerRegistration> {
  return container.register(swUrl, { scope: '/' });
}

/** Long enough for a first-visit install, short enough that a browser which will
 *  never register a worker still answers inside a screen's patience. */
export const SERVICE_WORKER_READY_MS = 5_000;

/**
 * `navigator.serviceWorker.ready` WITH A DEADLINE — the only way this app reads it.
 *
 * `ready` resolves when a worker is active and OTHERWISE NEVER SETTLES: it does
 * not reject when registration failed, was disabled, or never happened, so a
 * `.catch()` beside it can never run. All three call sites below awaited it bare,
 * and `/account/notifications` rendered "Your devices" as a permanent skeleton
 * wherever no worker registers — `vite dev` (`main.tsx` registers under
 * `import.meta.env.PROD` only), private windows, a throwing worker, SW disabled.
 *
 * kit's `withDeadline` bounds the WAIT, not the work: a registration landing late
 * still controls the page, the caller just already answered. Callers map the typed
 * `DeadlineExceededError` to "this browser can't say" (`probeCurrentDevice`),
 * never to an error state — a browser with no worker is not a failure.
 */
export function readyServiceWorker(
  container: ServiceWorkerContainer = navigator.serviceWorker,
  ms: number = SERVICE_WORKER_READY_MS,
): Promise<ServiceWorkerRegistration> {
  return withDeadline(container.ready, ms, 'navigator.serviceWorker.ready');
}

/** `GET /v1/push/public-key` → the VAPID key. The api boot-requires it (plan 101
 *  10-14), so any failure propagates and the caller surfaces it. */
export async function fetchVapidPublicKey(token: string | null): Promise<string> {
  const { public_key } = await apiFetch<{ public_key: string }>('/v1/push/public-key', token);
  return public_key;
}

/** The browser-facing seam. `browserPushRuntime` implements it against the real
 *  APIs; tests inject a fake to drive `enablePush` / `disablePush`. */
export type PushRuntime = {
  supported(): boolean;
  permission(): NotificationPermission;
  fetchPublicKey(): Promise<string>;
  requestPermission(): Promise<NotificationPermission>;
  subscribe(publicKeyBase64: string): Promise<PushSubscriptionJSON>;
  postSubscription(json: PushSubscriptionJSON): Promise<void>;
  /** The endpoint of the current subscription on this browser, or `null`. */
  currentEndpoint(): Promise<string | null>;
  /** Unsubscribe locally; returns the removed endpoint (for server cleanup) or `null`. */
  unsubscribe(): Promise<string | null>;
  deleteSubscription(endpoint: string): Promise<void>;
};

/** Outcome of an enable attempt — the toggle maps each to a UI state. */
export type EnableResult = 'subscribed' | 'unsupported' | 'denied';

/**
 * The reasons an {@link enablePush} attempt can fail visibly to the user (#5884).
 * Every reason is mapped from a specific step of the ladder; the dashboard
 * renders one i18n sentence per reason, and the test fence (`push-failure.test.ts`)
 * names every reason so a new step can't ship without naming its own failure.
 */
export const PUSH_ENABLE_FAILURES = [
  'service_worker_unavailable',
  'key_unavailable',
  'subscribe_refused',
  'register_failed',
] as const;
export type PushEnableFailure = (typeof PUSH_ENABLE_FAILURES)[number];

/**
 * A typed throw from {@link enablePush} (`#5884`). The reason narrows what the
 * UI shows; the underlying error is preserved on `cause` for the dev console.
 */
export class PushEnableError extends Error {
  override readonly name = 'PushEnableError';
  readonly reason: PushEnableFailure;
  override readonly cause: unknown;
  constructor(reason: PushEnableFailure, cause: unknown) {
    super(`push: enable failed (${reason})`);
    this.reason = reason;
    this.cause = cause;
  }
}

/**
 * Map a rejection from `subscribe`: the bounded service-worker wait (kit's
 * `DeadlineExceededError`, raised by `readyServiceWorker`) is
 * `service_worker_unavailable`; anything else is the browser's push service
 * refusing, `subscribe_refused`.
 */
function subscribeFailure(err: unknown): PushEnableError {
  return new PushEnableError(
    err instanceof DeadlineExceededError ? 'service_worker_unavailable' : 'subscribe_refused',
    err,
  );
}

/**
 * One line for the console: the error's name and message and nothing else, so no
 * subscription, endpoint or key can ride along in an object dump.
 */
export function errorSummary(err: unknown): string {
  return err instanceof Error ? `${err.name}: ${err.message}` : String(err);
}

/**
 * Enable push: check support → request permission → fetch the key → subscribe →
 * register the target server-side. The permission prompt MUST come BEFORE the
 * key fetch: the prompt is the only browser call that REQUIRES the click
 * handler to be on the call stack, so we ask while the handler is still here
 * (#5884). A short-circuit before it returns `unsupported` without prompting
 * (no browser can do push → no user-facing message needed).
 *
 * The key fetch, the subscribe and the POST each throw a typed
 * {@link PushEnableError} with a named reason (the permission step keeps its
 * result values, `denied` and `unsupported`). A POST that fails after a
 * subscription THIS attempt created unsubscribes it locally before throwing
 * `register_failed`, so a half-registered state (browser subscribed, server
 * doesn't know) can't linger; a subscription the browser already held is left
 * alone.
 */
export async function enablePush(rt: PushRuntime): Promise<EnableResult> {
  if (!rt.supported()) return 'unsupported';
  // Permission first — it MUST stay inside the click handler that called
  // `enable()`. Prompts that resolve to `denied` are still a clean refusal,
  // not an error.
  if ((await rt.requestPermission()) !== 'granted') return 'denied';
  let key: string;
  try {
    key = await rt.fetchPublicKey();
  } catch (e: unknown) {
    throw new PushEnableError('key_unavailable', e);
  }
  // `subscribe` hands back the browser's EXISTING subscription when one is held for
  // the same key (another tab, an init that timed out), and the server may already
  // know it. Only a subscription this attempt creates is ours to roll back, so
  // look first; a read that fails is "unknown", and unknown is left alone.
  const held = await rt.currentEndpoint().catch(() => undefined);
  let subscription: PushSubscriptionJSON;
  try {
    subscription = await rt.subscribe(key);
  } catch (e: unknown) {
    throw subscribeFailure(e);
  }
  try {
    await rt.postSubscription(subscription);
  } catch (e: unknown) {
    // The browser is subscribed but the server doesn't know. When this attempt
    // created the subscription, roll it back so the next click is a clean attempt,
    // not a repair.
    if (held === null) {
      try {
        await rt.unsubscribe();
      } catch (rollback: unknown) {
        // The reason stays register_failed; the console says the rollback did not land.
        console.warn('[dashboard] push: rollback unsubscribe failed', errorSummary(rollback));
      }
    }
    throw new PushEnableError('register_failed', e);
  }
  return 'subscribed';
}

/**
 * Disable push: delete the server-side target FIRST, then unsubscribe locally.
 * Deleting first means a failed DELETE keeps the local endpoint around to retry —
 * the reverse order would strand a live server row (worker keeps fanning out to a
 * dead endpoint) with no local handle to prune it. A no-op when nothing is
 * subscribed here.
 */
export async function disablePush(rt: PushRuntime): Promise<void> {
  const endpoint = await rt.currentEndpoint();
  if (endpoint === null) return;
  await rt.deleteSubscription(endpoint);
  await rt.unsubscribe();
}

/**
 * The budget for each of {@link retirePushOnSignOut}'s two bounded waits — the
 * whole of {@link disablePush}, then (only if that failed or timed out) one local
 * unsubscribe — so sign-out waits at most 2 × this on push. None of the calls
 * inside has a deadline of its own (`deleteSubscription` is a plain fetch).
 */
export const PUSH_RETIRE_MS = 3_000;

/**
 * Retire this browser's push subscription at sign-out (05-7, plan 101). A
 * subscription belongs to the USER, not the session, so without this the browser
 * kept receiving — and the service worker kept showing, titles and bodies — the
 * signed-out user's notifications, to whoever used it next.
 *
 * Runs BEFORE the logout POST: the server delete needs the live session. Unlike
 * {@link disablePush}, a failed delete does not keep the local subscription —
 * leaving the browser subscribed is the leak this closes, and a dead endpoint's
 * server row is pruned on its next send (410).
 *
 * Never rejects, and never waits longer than 2 × `ms`: the first `withDeadline`
 * bounds ALL of `disablePush` (read the endpoint, delete the server row, then
 * unsubscribe) as one `ms` budget, and a disable that failed or timed out gets
 * ONE more bounded wait of `ms` for the local unsubscribe. `withDeadline` bounds
 * the wait, not the work; a call landing late is harmless. A sign-out must not
 * be blocked by push cleanup.
 */
export async function retirePushOnSignOut(
  rt: PushRuntime,
  ms: number = PUSH_RETIRE_MS,
): Promise<void> {
  if (!rt.supported()) return;
  try {
    await withDeadline(disablePush(rt), ms, 'push retire: disable');
  } catch (err) {
    console.warn('[dashboard] push: server delete at sign-out failed', err);
    await withDeadline(rt.unsubscribe(), ms, 'push retire: local unsubscribe').catch(
      (e: unknown) => {
        console.warn('[dashboard] push: local unsubscribe at sign-out failed', e);
      },
    );
  }
}

/**
 * The real browser runtime, bound to a session token for the API calls. State
 * machines are tested against a fake `PushRuntime` instead of this, so its use of
 * `navigator` / `Notification` at call time is safe where those exist. `ready` IS
 * injected here, by one case only (`push.test.ts`): "every hop that needs the
 * registration is bounded" is a claim about THIS object, and a fake seam cannot
 * make it.
 */
export function browserPushRuntime(
  token: string | null = SESSION_TOKEN,
  ready: () => Promise<ServiceWorkerRegistration> = readyServiceWorker,
): PushRuntime {
  return {
    supported: () => pushSupported(),
    permission: () => (typeof Notification === 'undefined' ? 'default' : Notification.permission),
    fetchPublicKey: () => fetchVapidPublicKey(token),
    requestPermission: () => Notification.requestPermission(),
    subscribe: async (publicKeyBase64) => {
      const reg = await ready();
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKeyBase64),
      });
      return sub.toJSON();
    },
    postSubscription: async (json) => {
      await apiFetch('/v1/push/subscriptions', token, {
        method: 'POST',
        body: JSON.stringify(json),
      });
    },
    currentEndpoint: async () => {
      const reg = await ready();
      const sub = await reg.pushManager.getSubscription();
      return sub?.endpoint ?? null;
    },
    unsubscribe: async () => {
      const reg = await ready();
      const sub = await reg.pushManager.getSubscription();
      if (!sub) return null;
      const { endpoint } = sub;
      await sub.unsubscribe();
      return endpoint;
    },
    deleteSubscription: async (endpoint) => {
      await apiFetch('/v1/push/subscriptions', token, {
        method: 'DELETE',
        body: JSON.stringify({ endpoint }),
      });
    },
  };
}
